import React from 'react';
import { Nav, PublicNav } from '../components/Nav';
import { Box, CircularProgress } from '@mui/material';
import {
  useAuthStatus,
  useCan,
  useGetUserInfoQuery,
  useUser,
  useUserOnboarding,
} from '@features/user';
import { UnauthorizedError } from '../components/errors/UnauthorizedError';
import { OnboardingView } from '@features/onboarding';
import { AddOnOnboardingHost, ADDON_ONBOARDINGS } from '@features/addon-onboarding';
import LoginView from './LoginView';
import SessionExpiredView from './SessionExpiredView';
import { useLocation } from 'react-router-dom';
import { StatsigUserGate } from '@services/statsig';
// Import the front-chat feature and widget
import { FrontChat } from '@features/front-chat';
import { EncounterNavigationGuard } from '../components/guards/EncounterNavigationGuard';
import { SystemNotificationBanner } from '@features/system-notifications';
import { LockedOutContent } from './LockedOutContent';
import { MembershipDeactivatedContent } from './MembershipDeactivatedContent';

const EXCEPTED_ROUTES = [
  '/record',
  '/encounters',
  '/user',
  '/group-invite',
  '/practice/clinicians',
  '/practice/subscriptions',
  '/bundles',
];

const isExceptedRoute = (pathname: string) => {
  return EXCEPTED_ROUTES.some(route => pathname.includes(route));
};

export const RootView = ({ children }: { children: any }) => {
  const { userState } = useUser();
  const authStatus = useAuthStatus();
  const shouldSkipQuery = authStatus === 'unauthenticated' || authStatus === 'initializing';
  const { data: userInfoData, isLoading: userQueryLoading } = useGetUserInfoQuery(undefined, {
    skip: shouldSkipQuery,
  });
  const isAdmin = useCan('manage_organization');
  const { onboardingSteps } = useUserOnboarding({ skip: shouldSkipQuery });
  const location = useLocation();
  const isExcepted = isExceptedRoute(location.pathname);

  const isFullScreenComponent =
    [
      '/',
      '/note/',
      '/templates',
      '/demo',
      '/patient',
      '/notes',
      '/group',
      '/patients',
      '/onboarding',
      '/dashboard',
      '/record',
      '/encounters',
      '/group-invite',
    ].some(path => window.location.pathname.startsWith(path)) || false;

  const userEmail = userState?.userInfo?.email || '';
  const paymentStatus = userState?.paymentStatus || '';

  // Determine which navigation to show
  const shouldShowFullNav = () => {
    // Show full Nav for:
    // - Fully authenticated users
    // - Locked out users (they're authenticated but payment issues)
    // - Excepted routes (special pages that always get full nav)
    return authStatus === 'authenticated' || authStatus === 'locked_out' || isExcepted;
  };

  // Show loading while checking authentication
  // Note: Statsig user sync is handled by StatsigUserGate component
  if (
    authStatus === 'initializing' ||
    authStatus === 'checking' ||
    (!shouldSkipQuery && userQueryLoading)
  ) {
    return (
      <Box
        id="root-container"
        className="rr-mask"
        sx={{
          height: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Route to appropriate view based on auth status
  const renderMainContent = () => {
    switch (authStatus) {
      case 'unauthenticated':
        return <LoginView />;

      case 'unauthorized':
        return <SessionExpiredView />;

      case 'error':
        return (
          <UnauthorizedError message="An authentication error occurred. Please log in again." />
        );

      case 'locked_out':
        // Content rendering logic - StatsigUserGate is handled at higher level
        // LockedOutContent handles feature gate checks after user sync completes
        return (
          <LockedOutContent
            paymentStatus={paymentStatus}
            userEmail={userEmail}
            isExcepted={isExcepted}
          >
            {children}
          </LockedOutContent>
        );

      case 'membership_deactivated':
        return <MembershipDeactivatedContent isAdmin={isAdmin} />;

      case 'authenticated':
        // Check if user needs to complete signup
        if (!onboardingSteps['account_creation_onboarding_completed']) {
          return <OnboardingView />;
        }
        // Wrap with navigation guard to intercept encounter navigation while recording.
        // AddOnOnboardingHost lives here (post first-run) so add-on onboarding only
        // fires after account-creation onboarding is complete.
        return (
          <EncounterNavigationGuard>
            {children}
            <AddOnOnboardingHost entries={ADDON_ONBOARDINGS} />
          </EncounterNavigationGuard>
        );

      default:
        console.log('Unknown auth status:', authStatus);
        return children;
    }
  };

  // Render authenticated/locked_out users with StatsigUserGate to ensure all feature gates
  // (including Nav) are checked after user sync completes
  if (
    authStatus === 'authenticated' ||
    authStatus === 'locked_out' ||
    authStatus === 'membership_deactivated'
  ) {
    return (
      <StatsigUserGate>
        <Box
          id="root-container"
          className="rr-mask"
          sx={{
            maxWidth: '100vw',
            overflowX: 'scroll',
            backgroundColor:
              authStatus === 'authenticated' &&
              !onboardingSteps['account_creation_onboarding_completed']
                ? 'rgba(213, 211, 224, 0.3)'
                : 'transparent',
            height: '100dvh',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <SystemNotificationBanner />
          {shouldShowFullNav() ? <Nav /> : <PublicNav />}
          <FrontChat />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              overflowX: 'hidden',
              height: '100%',
              width: '100%',
            }}
            id="root-main"
          >
            <Box
              sx={{
                width: {
                  xs: isFullScreenComponent ? '100%' : '95%',
                  md: isFullScreenComponent ? '100%' : '80%',
                },
                height: '100%',
              }}
            >
              {renderMainContent()}
            </Box>
          </Box>
        </Box>
      </StatsigUserGate>
    );
  }

  // Render unauthenticated users without StatsigUserGate (anonymous user is fine)
  return (
    <Box
      id="root-container"
      className="rr-mask"
      sx={{
        maxWidth: '100vw',
        overflowX: 'scroll',
        backgroundColor: 'transparent',
        height: '100dvh',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <SystemNotificationBanner />
      <PublicNav />
      <FrontChat />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          overflowX: 'hidden',
          height: '100%',
          width: '100%',
        }}
        id="root-main"
      >
        <Box
          sx={{
            width: {
              xs: isFullScreenComponent ? '100%' : '95%',
              md: isFullScreenComponent ? '100%' : '80%',
            },
            height: '100%',
          }}
        >
          {renderMainContent()}
        </Box>
      </Box>
    </Box>
  );
};
